import type { QueryClient } from '@tanstack/react-query';

export class CommentReadError extends Error {
  constructor(
    code: string,
    public retryAt = 0,
  ) {
    super(code);
  }
}

export async function readCommentResponse<T>(response: Response): Promise<T> {
  const data = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    const delay = Number(response.headers.get('retry-after'));
    const retryAt =
      response.status === 429
        ? Date.now() + (Number.isFinite(delay) && delay > 0 ? delay : 60) * 1000
        : 0;
    throw new CommentReadError(data.error || 'request-failed', retryAt);
  }
  return data;
}

function waitForReadSlot(milliseconds: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
  });
}

export function createCommentReadScheduler({
  now = Date.now,
  wait = waitForReadSlot,
}: {
  now?: () => number;
  wait?: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
} = {}) {
  let starts: number[] = [];
  let queue = Promise.resolve();
  return async function read<T>(
    request: () => Promise<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    const admission = queue.then(async () => {
      signal?.throwIfAborted();
      while (true) {
        starts = starts.filter((start) => start > now() - 61000);
        if (starts.length < 60) break;
        await wait(starts[0]! + 61000 - now(), signal);
        signal?.throwIfAborted();
      }
      starts.push(now());
    });
    queue = admission.catch(() => {});
    await admission;
    signal?.throwIfAborted();
    return request();
  };
}

const scheduleRead = createCommentReadScheduler();

export function fetchCommentRead<T>(
  url: string,
  signal?: AbortSignal,
): Promise<T> {
  // Shared admission covers polling, focus/reconnect and mutation invalidation.
  // It also bounds one large infinite-query refresh, regardless of its pages.
  return scheduleRead(
    async () =>
      readCommentResponse<T>(
        await fetch(url, {
          cache: 'no-store',
          credentials: 'same-origin',
          signal,
        }),
      ),
    signal,
  );
}

export function retryCommentRead(failureCount: number, error: Error) {
  return (
    error.message !== 'rate-limited' &&
    !('code' in error && error.code === 'TOO_MANY_REQUESTS') &&
    failureCount < 1
  );
}

export function commentReadInterval(error: Error | null, interval: number) {
  if (
    error &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'retryAfterSeconds' in error.data &&
    typeof error.data.retryAfterSeconds === 'number'
  )
    return Math.max(interval, error.data.retryAfterSeconds * 1000);
  return error instanceof CommentReadError && error.retryAt > Date.now()
    ? Math.max(interval, error.retryAt - Date.now())
    : interval;
}

export function commentPollingInterval(
  client: QueryClient,
  error: Error | null,
  minimum: number,
) {
  const pages = client
    .getQueryCache()
    .getAll()
    .reduce((total, query) => {
      if (!query.isActive() || !query.meta?.commentPolling) return total;
      const data = query.state.data;
      const count =
        data &&
        typeof data === 'object' &&
        'pages' in data &&
        Array.isArray(data.pages)
          ? data.pages.length
          : 1;
      return total + Math.max(1, count);
    }, 0);
  // Infinite-query polling reloads every cached page. Budget all open threads
  // together at <=30 background requests/minute, leaving room for user actions.
  return commentReadInterval(error, Math.max(minimum, pages * 2000));
}
